import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import propertiesService from "../services/propertiesService";
import documentsService from "../services/documentsService";
import mockApi from "../services/mockApi";
import styles from "./PropertyDocuments.module.css";

const PropertyDocuments = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [property, setProperty] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [processDocuments, setProcessDocuments] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);

  // Load property data
  useEffect(() => {
    loadPropertyData();
  }, [id]);

  // Load documents data
  useEffect(() => {
    loadDocumentsData();
  }, [currentPage]);

  const loadPropertyData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use mock API for testing
      const response = mockApi.getProperty(id);
      if (response.success) {
        setProperty(response.data);
      } else {
        throw new Error("Failed to load property");
      }
    } catch (error) {
      console.error("Error loading property data:", error);
      setError("Error loading property data");
      toast.error("Error loading property data");
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentsData = async () => {
    try {
      // Use real API first, fallback to mock if needed
      let documentsResponse, certificatesResponse, processDocumentsResponse;

      try {
        // Try real API first - only call the documents endpoint
        documentsResponse = await documentsService.getPropertyDocuments(
          id,
          currentPage
        );
        
        console.log("📄 [FRONTEND] Real API response:", documentsResponse);
        
        // Map the real API response structure
        if (documentsResponse && documentsResponse.success && documentsResponse.data) {
          // The API returns an array of documents directly in data
          // Set all documents in the documents array (no separate certificates)
          setDocuments(documentsResponse.data);
          setCertificates([]); // No separate certificates endpoint
          console.log("✅ [FRONTEND] Set documents from real API:", documentsResponse.data.length, "documents");
        } else {
          setDocuments([]);
          setCertificates([]);
        }

        // Process documents are still using mock for now
        processDocumentsResponse = mockApi.getProcessDocuments();
        if (processDocumentsResponse.success) {
          setProcessDocuments(processDocumentsResponse.data);
        }

        // Set pagination if available
        if (documentsResponse.pagination) {
          setPagination(documentsResponse.pagination);
        } else {
          // Set default pagination based on data length
          setPagination({
            current_page: 1,
            last_page: 1,
            per_page: 10,
            total: documentsResponse.data ? documentsResponse.data.length : 0,
          });
        }
      } catch (apiError) {
        console.log("⚠️ [FRONTEND] API failed, using mock data:", apiError.message);
        
        // Fallback to mock API for testing
        documentsResponse = mockApi.getPropertyDocuments(id);
        certificatesResponse = mockApi.getPropertyCertificates(id);
        processDocumentsResponse = mockApi.getProcessDocuments();

        if (documentsResponse.success) {
          setDocuments(documentsResponse.data);
          if (documentsResponse.pagination) {
            setPagination(documentsResponse.pagination);
          }
        }

        if (certificatesResponse.success) {
          setCertificates(certificatesResponse.data);
        }

        if (processDocumentsResponse.success) {
          setProcessDocuments(processDocumentsResponse.data);
        }
      }
    } catch (error) {
      console.error("Error loading documents:", error);
      toast.error("Error loading documents");
    }
  };

  const handleDownload = async (document) => {
    try {
      // ✅ FIXED: Backend now redirects to presigned URL
      try {
        // Use documentsService which handles the redirect and returns presigned URL
        const presignedUrl = await documentsService.downloadPropertyDocument(id, document.id);
        
        if (presignedUrl) {
          // Create download link with presigned URL
          const link = document.createElement("a");
          link.href = presignedUrl;
          link.setAttribute("download", document.name);
          link.target = "_blank"; // Open in new tab for direct S3 access
          document.body.appendChild(link);
          link.click();
          link.remove();
          toast.success(`Downloaded ${document.name} successfully`);
        } else {
          throw new Error("No presigned URL received");
        }
      } catch (apiError) {
        console.error("API download error:", apiError);
        // Fallback to mock API for testing
        const response = mockApi.downloadPropertyDocument(id, document.id);
        if (response.success) {
          toast.success(`Downloaded ${document.name} successfully`);
        } else {
          toast.error(response.message || "Error downloading document");
        }
      }
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Error downloading document");
    }
  };

  const handleEdit = (document) => {
    // Navigate to edit page
    navigate(`/properties/documents/${document.id}/edit`);
  };

  const handleDelete = async (document) => {
    if (window.confirm(`Are you sure you want to remove "${document.name}"?`)) {
      try {
        // Use real API or mock API for testing
        let response;
        try {
          response = await documentsService.deletePropertyDocument(
            id,
            document.id
          );
          toast.success(`${document.name} deleted successfully`);
          loadDocumentsData();
        } catch (apiError) {
          // Fallback to mock API for testing
          response = mockApi.deletePropertyDocument(id, document.id);
          if (response.success) {
            toast.success(`${document.name} deleted successfully`);
            loadDocumentsData();
          } else {
            toast.error(response.message || "Error deleting document");
          }
        }
      } catch (error) {
        console.error("Error deleting document:", error);
        toast.error("Error deleting document");
      }
    }
  };

  const handleAddDocument = () => {
    navigate(`/properties/${id}/documents/create`);
  };

  const handleAddCertificate = () => {
    navigate(`/properties/${id}/certificate/create`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h3>Error</h3>
          <p>{error}</p>
          <button
            onClick={() => navigate(`/properties/${id}`)}
            className={styles.btnSecondary}
          >
            Back to Property
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          Properties
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          {property?.name}
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Documents</span>
        </div>
      </div>

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h3 className={styles.panelTitle}>
            <span className={styles.textMuted}>Property documents (</span>
            {property?.name}
            <span className={styles.textMuted}>)</span>
          </h3>
        </div>
        <div className={styles.headerRight}>
          <button onClick={handleAddDocument} className={styles.btnSecondary}>
            <i className={styles.glyphiconPlus}></i>
            Add document
          </button>
          <button
            onClick={handleAddCertificate}
            className={styles.btnSecondary}
          >
            <i className={styles.glyphiconPlus}></i>
            Add certificate
          </button>
        </div>
      </div>

      <div className={styles.panelGroup}>
        <div className={styles.panelBody}>
          {console.log("🔍 [FRONTEND] Render check - certificates:", certificates.length, "documents:", documents.length)}
          {certificates.length > 0 || documents.length > 0 ? (
            <table
              id="property-documents"
              data-property={id}
              className={styles.table}
            >
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Combine certificates and documents into one list */}
                {[...certificates, ...documents].map((document, index) => (
                  <tr key={`doc-${document.id}-${index}`} data-id={document.id}>
                    <td>
                      <a
                        href="#"
                        className={styles.hiddenPrintLink}
                        onClick={(e) => {
                          e.preventDefault();
                          handleDownload(document);
                        }}
                      >
                        {document.name || document.document_name}
                      </a>
                    </td>
                    <td>{formatDate(document.created_at || document.updated_at)}</td>
                    <td>
                      <a
                        href="#"
                        className={styles.hiddenPrintLink}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/properties/${id}/documents/${document.id}/edit`);
                        }}
                      >
                        <div
                          className={`${styles.documentAction} ${styles.documentActionLeft} ${styles.documentActionEdit} ${styles.hiddenPrint}`}
                        >
                          Edit
                        </div>
                      </a>
                      <button
                        className={`${styles.documentAction} ${styles.documentActionRight} ${styles.documentActionDelete} ${styles.hiddenPrint}`}
                        onClick={() => handleDelete(document)}
                        data-confirm-action="Are you sure you want to remove?"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.noDocuments}>No documents!</div>
          )}

          {/* Pagination */}
          {(pagination.last_page > 1 || (certificates.length + documents.length) > 0) && (
            <div className={styles.pagination}>
              <div className={styles.paginationInfo}>
                Showing{" "}
                {(pagination.current_page - 1) * pagination.per_page + 1} to{" "}
                {Math.min(
                  pagination.current_page * pagination.per_page,
                  pagination.total || (certificates.length + documents.length)
                )}{" "}
                of {pagination.total || (certificates.length + documents.length)} documents
              </div>
              <div className={styles.paginationControls}>
                <button
                  className={`${styles.paginationBtn} ${
                    pagination.current_page === 1
                      ? styles.paginationBtnDisabled
                      : ""
                  }`}
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                >
                  Previous
                </button>
                {Array.from(
                  { length: pagination.last_page },
                  (_, i) => i + 1
                ).map((page) => (
                  <button
                    key={page}
                    className={`${styles.paginationBtn} ${
                      pagination.current_page === page
                        ? styles.paginationBtnActive
                        : ""
                    }`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  className={`${styles.paginationBtn} ${
                    pagination.current_page === pagination.last_page
                      ? styles.paginationBtnDisabled
                      : ""
                  }`}
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.last_page}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          <table className={`${styles.table} ${styles.mt30}`}>
            <thead>
              <tr>
                <th>Name</th>
              </tr>
            </thead>
            <tbody>
              {processDocuments.map((doc, index) => (
                <tr key={index}>
                  <td>
                    <a
                      href="#"
                      className={styles.hiddenPrintLink}
                      onClick={(e) => {
                        e.preventDefault();
                        handleDownload(doc);
                      }}
                    >
                      {doc.name}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PropertyDocuments;
